import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { TableMap } from '@tiptap/pm/tables'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

import { cellShading, gridBeforeOf, tblStyleInfo } from '../ooxmlCss.js'
import { tableStyleOf } from '../studioContext.js'

/**
 * Where each table cell sits, as classes on the cell, for the table rules of
 * studio.css and the table-style rules studioStylesheets.js writes (RED-317
 * PR 3c-E). Word draws a cell from its place in the table:
 *
 * - `tb-first-row` / `tb-last-row` / `tb-first-col` / `tb-last-col`: the
 *   cell is on the table's outer edge, and that edge takes the table's outer
 *   border (tblBorders top/bottom/left/right) instead of insideH/V. The table
 *   box draws no border of its own, so a cell's tcBorders can switch an outer
 *   line off (nil) like Word does; drawn on the table box, the line won the
 *   border collapse whatever the cell said.
 * - `ts-*`: the conditional regions of the bound table style (w:tblStylePr)
 *   that the table's w:tblLook switches on (`ts-in-top` / `-bottom` /
 *   `-left` / `-right`: that edge is inside a band of several rows or
 *   columns, where the region draws its insideH/insideV). Measured in Word.app 16.112: the
 *   header row and the total row (when on) are left out of row banding and
 *   the first and last column out of column banding, bands count
 *   tblStyleRowBandSize / tblStyleColBandSize rows or columns, and a corner
 *   region applies only when both of its sides are on (nwCell = first row
 *   and first column). Which region wins is the rule order of the style
 *   sheet: row bands < column bands < first/last column < first/last row <
 *   corners.
 * - `studio-vmerge-above`: the cell below continues this one (vMerge), so
 *   no line between them.
 *
 * A vMerge continuation is drawn as part of the cell it continues, as in
 * Word (measured in Word.app 16.112): the restart cell's table-style regions
 * (a band counted from the restart's row) and the restart's own shading,
 * which the continuation carries as decoration attributes (`style`,
 * `data-fill-tone`) while renderCell leaves its own shading out. Its edge
 * classes stay its own: the merged cell's outer lines are where it ends.
 *
 * A row that skips grid columns at its end (w:gridAfter) ends on its last
 * cell, which takes the right border as in Word. A row that skips grid
 * columns at its start (w:gridBefore) begins with an empty, borderless,
 * uneditable spacer cell (a widget, `studio-grid-before`) as wide as the
 * skipped columns, so its first cell sits at its own grid column; that cell
 * still takes the table's left border and the first-column region, as Word
 * draws it. Purely a view concern: nothing reaches the document or the
 * compiler. Cached per table node, so an edit recomputes only the tables it
 * changed.
 */

const cellsCache = new WeakMap()
const spacersCache = new WeakMap()

/**
 * The rows that skip grid columns before their first cell (w:gridBefore):
 * where the row's content starts (relative to the table's content start)
 * and how many columns it skips.
 *
 * @returns {Array<{ offset: number, columns: number }>}
 */
export function tableGridBefore(table) {
  let spacers = spacersCache.get(table)
  if (spacers) return spacers
  spacers = []
  table.forEach((row, offset) => {
    const columns = gridBeforeOf(row.attrs.trPr)
    if (columns > 0) spacers.push({ offset: offset + 1, columns })
  })
  spacersCache.set(table, spacers)
  return spacers
}

/** The spacer cell of a gridBefore row: as wide as the columns it skips, no border, no padding, no caret. */
function gridBeforeSpacer(columns) {
  return () => {
    const spacer = document.createElement('td')
    spacer.className = 'studio-grid-before'
    spacer.colSpan = columns
    spacer.setAttribute('aria-hidden', 'true')
    return spacer
  }
}

/**
 * The classes of every cell of a table, by the cell's offset in the table
 * (TableMap's positions: relative to the table's content start), plus the
 * shading a vMerge continuation takes from the cell it continues (`style`,
 * `tone`; null for every other cell).
 *
 * @returns {Array<{ offset: number, size: number, classes: string, style: string|null, tone: string|null }>}
 */
export function tableCellClasses(table) {
  let cells = cellsCache.get(table)
  if (cells) return cells

  const map = TableMap.get(table)
  const { width, height } = map
  const style = table.attrs.tblPr ? tblStyleInfo(table.attrs.tblPr) : null
  const look = style?.look ?? null
  const bands = style ? tableStyleOf(style.styleId) : null
  const rowBand = Math.max(1, Number(bands?.rowBandSize) || 1)
  const colBand = Math.max(1, Number(bands?.colBandSize) || 1)

  // Where each row's cells end: a missing position (0) is a skipped grid column.
  const rowEnds = Array.from({ length: height }, (_, row) => {
    let end = width
    while (end > 0 && map.map[row * width + end - 1] === 0) end--
    return end
  })

  const edgesOf = ({ left, right, top, bottom }) => ({ top: top === 0, bottom: bottom === height, left: left === 0, right: right >= rowEnds[top] })

  /** The table-style regions (ts-* classes) of a cell at this rectangle, in Word's order of application. */
  const regionClasses = (rect) => {
    if (!look) return []
    const edge = edgesOf(rect)
    const classes = []
    const region = {
      firstRow: look.firstRow && edge.top,
      lastRow: look.lastRow && edge.bottom,
      firstCol: look.firstColumn && edge.left,
      lastCol: look.lastColumn && edge.right,
    }
    if (!look.noHBand && !region.firstRow && !region.lastRow) {
      const first = look.firstRow ? 1 : 0
      const band = Math.floor((rect.top - first) / rowBand) % 2
      classes.push(band === 0 ? 'ts-band1-h' : 'ts-band2-h')
      // Inside a band of several rows: that edge takes the region's insideH (studioStylesheets.js).
      if ((rect.top - first) % rowBand !== 0) classes.push('ts-in-top')
      if ((rect.bottom - first) % rowBand !== 0 && rect.bottom < height - (look.lastRow ? 1 : 0)) classes.push('ts-in-bottom')
    }
    if (!look.noVBand && !region.firstCol && !region.lastCol) {
      const first = look.firstColumn ? 1 : 0
      const band = Math.floor((rect.left - first) / colBand) % 2
      classes.push(band === 0 ? 'ts-band1-v' : 'ts-band2-v')
      // Inside a band of several columns: that edge takes the region's insideV.
      if ((rect.left - first) % colBand !== 0) classes.push('ts-in-left')
      if ((rect.right - first) % colBand !== 0 && rect.right < rowEnds[rect.top] - (look.lastColumn ? 1 : 0)) classes.push('ts-in-right')
    }
    if (region.firstCol) classes.push('ts-first-col')
    if (region.lastCol) classes.push('ts-last-col')
    if (region.firstRow) classes.push('ts-first-row')
    if (region.lastRow) classes.push('ts-last-row')
    if (region.firstRow && region.firstCol) classes.push('ts-nw')
    if (region.firstRow && region.lastCol) classes.push('ts-ne')
    if (region.lastRow && region.firstCol) classes.push('ts-sw')
    if (region.lastRow && region.lastCol) classes.push('ts-se')
    return classes
  }

  // TableMap lays a gridBefore row's cells from column 0; their grid column is gridBefore further right.
  const gridBefore = []
  table.forEach((row) => gridBefore.push(gridBeforeOf(row.attrs.trPr)))

  /** The cell a vMerge continuation continues: the nearest cell above it, in its grid column, that is not a continuation. */
  const restartAbove = ({ left, top }) => {
    const column = left + gridBefore[top]
    for (let row = top - 1; row >= 0; row--) {
      const mapColumn = column - gridBefore[row]
      const offset = mapColumn >= 0 && mapColumn < width ? map.map[row * width + mapColumn] : 0
      const cell = offset ? table.nodeAt(offset) : null
      if (!cell) return null
      if (cell.attrs.vMerge !== 'continue') return { cell, rect: map.findCell(offset) }
    }
    return null
  }

  cells = []
  const seen = new Set()
  for (const offset of map.map) {
    if (offset === 0 || seen.has(offset)) continue
    seen.add(offset)
    const cell = table.nodeAt(offset)
    const rect = map.findCell(offset)
    const edge = edgesOf(rect)
    const classes = []
    if (edge.top) classes.push('tb-first-row')
    if (edge.bottom) classes.push('tb-last-row')
    if (edge.left) classes.push('tb-first-col')
    if (edge.right) classes.push('tb-last-col')

    const restart = cell.attrs.vMerge === 'continue' ? restartAbove(rect) : null
    classes.push(...regionClasses(restart ? restart.rect : rect))

    const below = rect.bottom < height ? map.map[rect.bottom * width + rect.left] : 0
    if (below && table.nodeAt(below)?.attrs.vMerge === 'continue') classes.push('studio-vmerge-above')

    const shading = restart ? cellShading(restart.cell.attrs.tcPr, restart.cell.attrs.fill) : null
    cells.push({ offset, size: cell.nodeSize, classes: classes.join(' '), style: shading?.css || null, tone: shading?.tone ?? null })
  }

  cellsCache.set(table, cells)
  return cells
}

/** The decoration set of a whole document. */
export function tableRegionDecorations(doc) {
  const decorations = []
  doc.descendants((node, pos) => {
    if (node.type.spec.tableRole === 'table') {
      for (const { offset, size, classes, style, tone } of tableCellClasses(node)) {
        if (!classes && !style && !tone) continue
        const attrs = { class: classes }
        if (style) attrs.style = style
        if (tone) attrs['data-fill-tone'] = tone
        decorations.push(Decoration.node(pos + 1 + offset, pos + 1 + offset + size, attrs))
      }
      for (const { offset, columns } of tableGridBefore(node)) {
        decorations.push(Decoration.widget(pos + 1 + offset, gridBeforeSpacer(columns), { side: -1, key: `studio-grid-before-${columns}`, ignoreSelection: true }))
      }
    }
    return !node.isTextblock
  })
  return DecorationSet.create(doc, decorations)
}

const tableRegionsKey = new PluginKey('studioTableRegions')

export const TableRegions = Extension.create({
  name: 'studioTableRegions',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: tableRegionsKey,
        state: {
          init: (_, state) => tableRegionDecorations(state.doc),
          apply: (tr, set) => (tr.docChanged ? tableRegionDecorations(tr.doc) : set),
        },
        props: {
          decorations: (state) => tableRegionsKey.getState(state),
        },
      }),
    ]
  },
})
